<template>
  <!-- Floating search over the brand catalog, pinned to the bottom of the hub
       page. Built from the generator's URL field (same ring, same icon slot,
       same trailing button) so the two inputs read as one control in two
       places — this one filters the dataset instead of extracting a palette.

       The dataset is small and already in the prerendered payload, so matching
       happens in memory: no request, results update as you type. -->
  <div class="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-4 pb-3">
    <!-- Scrim: the field floats over scrolling cards, so fade the page into the
         bottom edge and give the control something to sit on. -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-40 bg-gradient-to-t from-gray-950 via-gray-950/85 to-transparent"
    ></div>

    <div class="pointer-events-auto mx-auto w-full max-w-2xl">
      <!-- Results panel, above the field, so the field never moves. -->
      <div
        v-if="query.trim() !== ''"
        class="mb-2 overflow-hidden rounded-xl border border-gray-800 bg-gray-900/95 shadow-2xl shadow-black/60 backdrop-blur"
      >
        <ul v-if="matches.length" role="list" class="max-h-80 divide-y divide-gray-800/70 overflow-y-auto">
          <li v-for="brand in matches" :key="brand.slug">
            <NuxtLink
              :to="`/brand-colors/${brand.slug}`"
              class="flex items-center gap-3 px-4 py-2.5 transition hover:bg-gray-800/70 focus-visible:-outline-offset-2"
              @click="onPick(brand)"
            >
              <img
                v-if="brand.favicon"
                :src="brand.favicon"
                :alt="''"
                aria-hidden="true"
                width="24"
                height="24"
                loading="lazy"
                class="h-6 w-6 shrink-0 rounded bg-white object-contain p-0.5"
              />
              <span v-else class="h-6 w-6 shrink-0 rounded bg-gray-800" aria-hidden="true"></span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-semibold text-white">{{ brand.name }}</span>
                <span class="block truncate text-xs text-gray-500">{{ brand.domain }}</span>
              </span>
              <span class="flex shrink-0 gap-1" aria-hidden="true">
                <span
                  v-for="color in brand.brandColors.slice(0, 4)"
                  :key="color.value"
                  class="h-4 w-4 rounded ring-1 ring-inset ring-white/20"
                  :style="{ backgroundColor: color.value }"
                ></span>
              </span>
            </NuxtLink>
          </li>
        </ul>

        <!-- Nothing in the catalog: the two things we can still offer. -->
        <div v-else class="px-4 py-4">
          <p class="text-sm font-semibold text-white">“{{ query.trim() }}” isn't in the dataset yet.</p>
          <p class="mt-1 text-xs leading-5 text-gray-400">
            The catalog covers {{ brands.length }} of the world's most-visited sites. Anything else can still be
            extracted live, or pulled straight from the API.
          </p>
          <div class="mt-3 flex flex-wrap gap-2">
            <BaseButton :href="generatorHref" size="sm" @click="onGenerate">
              Generate live brand colors
            </BaseButton>
            <BaseButton :href="apiUrl('search_miss', route.path)" variant="soft" size="sm" @click="onMissApi">
              Get an API key
            </BaseButton>
          </div>
        </div>
      </div>

      <label for="brand-search" class="sr-only">Search the brand catalog</label>
      <InputGroup
        id="brand-search"
        v-model="query"
        elevated
        type="text"
        autocomplete="off"
        placeholder="Search a brand or domain…"
        @keydown.enter="onEnter"
        @keydown.escape="query = ''"
      >
        <template #leading>
          <img width="24" height="24" class="h-6 w-6" :src="paletteIcon" alt="" aria-hidden="true" />
        </template>
        <template #trailing>
          <TextLink v-if="query !== ''" tone="muted" class="m-2 rounded-md px-2 py-2 text-xs font-semibold" @click="query = ''">
            Clear
          </TextLink>
          <span v-else class="m-2 hidden shrink-0 rounded-md bg-gray-800 px-3 py-2 text-xs font-semibold text-gray-400 sm:block">
            {{ brands.length }} brands
          </span>
        </template>
      </InputGroup>

      <!-- The search is pinned, so this is the one piece of attribution that
           stays on screen the whole time the catalog is being browsed. -->
      <PrefetchCredit location="search bar" class="mt-1 justify-center" />
    </div>
  </div>
</template>

<script setup>
import { computed, onScopeDispose, ref, watch } from 'vue'
import { trackEvent } from '~/composables/analytics'
import BaseButton from '~/components/atoms/BaseButton.vue'
import TextLink from '~/components/atoms/TextLink.vue'
import InputGroup from '~/components/molecules/InputGroup.vue'
import PrefetchCredit from '~/components/molecules/PrefetchCredit.vue'
import paletteIcon from '~/assets/img/palettes_icon_bw.svg'
import { apiUrl } from '~/utils/site'

const props = defineProps({
  // BrandSummary[] from server/utils/brands.ts.
  brands: { type: Array, required: true },
})

const MAX_RESULTS = 8

// Only for stamping the outbound Prefetch links with the page they were clicked
// from; the in-app search events don't need it (this bar only exists on the hub).
const route = useRoute()

const query = ref('')

const normalized = computed(() => query.value.trim().toLowerCase())

const matches = computed(() => {
  const term = normalized.value
  if (!term) return []
  // A pasted URL should find the brand too, so match on the bare host.
  const host = term.replace(/^https?:\/\//, '').replace(/^www\./, '').split(/[/?#]/)[0]
  return props.brands
    .filter((brand) => brand.name.toLowerCase().includes(term) || brand.domain.includes(host || term))
    .slice(0, MAX_RESULTS)
})

// No match: hand the typed text straight to the generator, which extracts it live.
const generatorHref = computed(() => `/?url=${encodeURIComponent(normalized.value)}`)

// Results update on every keystroke, so a raw watcher would report "goo", "goog",
// "googl", "google" as four searches. Reporting the term once it stops changing
// gives one event per thing someone actually looked for — and, when it comes back
// empty, the list of brands the catalog is missing.
const SEARCH_SETTLE_MS = 900
let settleTimer = null
let lastTracked = ''

watch(normalized, (term) => {
  clearTimeout(settleTimer)
  if (!term) {
    // Cleared field: the next term is a new search even if it repeats this one.
    lastTracked = ''
    return
  }
  settleTimer = setTimeout(() => {
    if (term === lastTracked) return
    lastTracked = term
    const found = matches.value.length
    try {
      trackEvent(found ? 'Brand Search Query' : 'Brand Search No Results', { query: term, results: found })
    } catch {}
  }, SEARCH_SETTLE_MS)
})

onScopeDispose(() => clearTimeout(settleTimer))

const onPick = (brand) => {
  try {
    trackEvent('Brand Search Result Opened', { brand: brand.slug, domain: brand.domain, query: normalized.value })
  } catch {}
  query.value = ''
}

const onGenerate = () => {
  try {
    trackEvent('Brand Search Miss Generate Clicked', { query: normalized.value })
  } catch {}
}

// The other half of the empty-state: the people a missing brand sends to the API
// rather than to the live generator.
const onMissApi = () => {
  try {
    trackEvent('Brand Search Miss API Clicked', { query: normalized.value })
  } catch {}
}

// Enter on a single match is the fast path; otherwise let the list do the work.
const router = useRouter()
const onEnter = () => {
  if (matches.value.length === 1) {
    const brand = matches.value[0]
    try {
      trackEvent('Brand Search Result Opened', {
        brand: brand.slug,
        domain: brand.domain,
        query: normalized.value,
        method: 'enter',
      })
    } catch {}
    router.push(`/brand-colors/${brand.slug}`)
    query.value = ''
  }
}
</script>
